import { useMemo } from "react"

import { commentatorNeon } from "./commentator-neon"

/** Generates a flat-top octagon SVG path — geometric doubled-line frame */
function octagonPath(cx: number, cy: number, radius: number): string {
  const pts: string[] = []
  for (let i = 0; i < 8; i++) {
    const angle = (Math.PI * 2 * i) / 8 - Math.PI / 2 + Math.PI / 8
    pts.push(
      `${cx + Math.cos(angle) * radius},${cy + Math.sin(angle) * radius}`,
    )
  }
  return `M ${pts[0]} L ${pts.slice(1).join(" L ")} Z`
}

const RING_SIZE = 72

export function SmallPortrait({
  commentatorKey,
  name,
  portraitUrl,
}: {
  commentatorKey: string
  name: string
  portraitUrl: string | null
}) {
  const c = commentatorNeon(commentatorKey)

  const ringPaths = useMemo(() => {
    const center = RING_SIZE / 2
    return {
      inner: octagonPath(center, center, 30),
      mid: octagonPath(center, center, 33),
      outer: octagonPath(center, center, 36),
    }
  }, [])

  return (
    <div className="relative" style={{ width: 56, height: 56, flexShrink: 0 }}>
      <svg
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        style={{
          position: "absolute",
          top: (56 - RING_SIZE) / 2,
          left: (56 - RING_SIZE) / 2,
          width: RING_SIZE,
          height: RING_SIZE,
          pointerEvents: "none",
        }}
      >
        {/* Outer line — dimmest */}
        <path
          d={ringPaths.outer}
          fill="none"
          stroke={c.soft}
          strokeWidth={1}
          strokeLinejoin="miter"
          style={{
            filter: `drop-shadow(0 0 8px ${c.soft})`,
          }}
        />
        {/* Middle line */}
        <path
          d={ringPaths.mid}
          fill="none"
          stroke={c.glow}
          strokeWidth={1}
          strokeLinejoin="miter"
          style={{
            filter: `drop-shadow(0 0 6px ${c.glow}) drop-shadow(0 0 14px ${c.soft})`,
          }}
        />
        {/* Inner line — brightest */}
        <path
          d={ringPaths.inner}
          fill="none"
          stroke={c.neon}
          strokeWidth={1}
          strokeLinejoin="miter"
          style={{ filter: `drop-shadow(0 0 3px ${c.neon})` }}
        />
      </svg>
      <div
        className="overflow-hidden rounded-full"
        style={{ width: 56, height: 56 }}
      >
        {portraitUrl ? (
          <img
            src={portraitUrl}
            alt={name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center text-lg font-black"
            style={{ background: "oklch(0.15 0.02 55)", color: c.neon }}
          >
            {name.charAt(0)}
          </div>
        )}
      </div>
    </div>
  )
}
